import { motion } from 'framer-motion';
import { useReveal } from '../../hooks/useReveal';
import { Globe, LayoutDashboard, Sparkles, ArrowUpRight } from 'lucide-react';

const ease = [0.22, 1, 0.36, 1];

const SERVICES = [
    {
        id: 'websites',
        ref: '01',
        kicker: 'Websites',
        Icon: Globe,
        title: 'Conversion-first websites.',
        lede: 'Marketing sites, e-commerce, event pages and landing pages — engineered around one job: turning attention into action.',
        capabilities: [
            'Conversion marketing sites',
            'Custom e-commerce',
            'Event & launch pages',
            'High-intent landing pages',
        ],
    },
    {
        id: 'apps',
        ref: '02',
        kicker: 'Web & Mobile Apps',
        Icon: LayoutDashboard,
        title: 'Custom apps & dashboards.',
        lede: 'When spreadsheets break and SaaS forces the wrong workflow, we build the platform your team should have had years ago.',
        capabilities: [
            'Custom dashboards & internal tools',
            'Web platforms with auth & roles',
            'Mobile apps (iOS & Android)',
            'API integrations & data pipelines',
        ],
    },
    {
        id: 'marketing',
        ref: '03',
        kicker: 'Digital Marketing',
        Icon: Sparkles,
        title: 'A growth engine, not just ads.',
        lede: 'Funnels, AI follow-up, tracking and reporting — wired into one system so every lead is captured, qualified and routed.',
        capabilities: [
            'Campaign funnels & paid acquisition',
            'AI receptionists & follow-up',
            'Lead tracking & attribution',
            'CRM & marketing automation',
        ],
    },
];

export function WhatWeBuild() {
    const ref = useReveal();
    return (
        <section
            id="what-we-build"
            data-testid="what-we-build-section"
            className="relative section overflow-hidden"
        >
            <div className="absolute inset-0 grid-bg grid-bg-fade opacity-40" />
            <div className="atmosphere opacity-60" />

            <div className="shell relative">
                <div ref={ref} className="reveal max-w-3xl">
                    <p className="eyebrow">What we build · Three services, one studio</p>
                    <h2 className="mt-6 font-display font-semibold text-[2.6rem] sm:text-[3.6rem] lg:text-[4.4rem] leading-[1.02] tracking-[-0.04em] text-white">
                        From{' '}
                        <span className="text-gradient-cyan">first click</span>
                        {' '}to{' '}
                        <span className="text-gradient-cyan">booked client.</span>
                    </h2>
                    <p className="mt-6 text-[17px] sm:text-[18px] leading-[1.65] text-slate-300 max-w-2xl">
                        We design, build and engineer the entire path —
                        websites, custom software and the marketing system that
                        connects them. One studio, end-to-end.
                    </p>
                </div>

                <div className="mt-16 lg:mt-20 grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6">
                    {SERVICES.map((s, i) => (
                        <ServiceCard key={s.id} {...s} idx={i} />
                    ))}
                </div>
            </div>
        </section>
    );
}

function ServiceCard({ id, ref: refLabel, kicker, Icon, title, lede, capabilities, idx }) {
    const reveal = useReveal();
    return (
        <motion.article
            ref={reveal}
            data-testid={`service-card-${id}`}
            className="reveal group relative card-glass p-7 lg:p-8 overflow-hidden"
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, delay: idx * 0.12, ease }}
        >
            <div
                className="pointer-events-none absolute -top-16 -right-16 w-56 h-56 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                style={{ background: 'radial-gradient(circle, rgba(6,182,212,0.18), transparent 65%)' }}
            />

            <div className="relative flex items-center justify-between">
                <p className="font-mono text-[10.5px] tracking-[0.24em] uppercase font-medium text-cyan-400">
                    {refLabel} · {kicker}
                </p>
                <div
                    className="grid place-items-center w-9 h-9 rounded-lg"
                    style={{
                        background: 'rgba(255,255,255,0.04)',
                        border: '1px solid rgba(255,255,255,0.10)',
                    }}
                >
                    <Icon className="w-4 h-4 text-cyan-400" strokeWidth={1.6} />
                </div>
            </div>

            <h3 className="relative mt-7 font-display font-semibold text-[1.6rem] sm:text-[1.85rem] tracking-[-0.025em] text-white leading-[1.15]">
                {title}
            </h3>
            <p className="relative mt-4 text-[15px] leading-[1.6] text-slate-400">
                {lede}
            </p>

            <ul className="relative mt-7 space-y-1">
                {capabilities.map((c, i) => (
                    <li
                        key={c}
                        className="flex items-baseline gap-3 py-2.5 border-t"
                        style={{ borderColor: i === 0 ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.05)' }}
                    >
                        <span className="font-mono text-[10px] tracking-[0.16em] uppercase font-medium text-slate-500 w-7 shrink-0">
                            0{i + 1}
                        </span>
                        <span className="text-[14.5px] text-slate-200">
                            {c}
                        </span>
                    </li>
                ))}
            </ul>

            <div className="relative mt-8 pt-5 border-t border-white/[0.06] flex items-center gap-2 text-cyan-400 text-[13px] font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <span>See work in this category</span>
                <ArrowUpRight className="w-3.5 h-3.5" />
            </div>
        </motion.article>
    );
}
